// Customer-facing invoice, reached from the link in the invoice email.
//
// Addressed by an unguessable token rather than order number + email: the
// old form put the customer's address in the URL, where it leaked into
// browser history, referrer headers and screenshots.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Printer } from 'lucide-react';
import {
  describeApiError,
  getOrderByInvoiceToken,
  getPaymentMethods,
  getPublicSettings,
} from '../services/api';
import InvoiceDocument, { invoiceBusiness } from '../components/InvoiceDocument';
import type { Order, PaymentMethodOption } from '../types';
import { STRIPE_METHOD_KEY } from '../types';
import { formatCents } from '../utils/money';
import { isNarrowScreen, renderInvoiceImage } from '../utils/invoiceImage';

const Invoice: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [order, setOrder] = useState<Order | undefined>();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodOption[]>([]);
  const [business, setBusiness] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  // A phone prints the page at its own screen width and scales that up to
  // the paper, so the invoice comes out in its stacked phone layout across
  // two sheets. On a narrow screen the printout is instead a picture of
  // the full-width invoice, sized to fit one page. Decided once: a phone
  // doesn't become a desktop mid-visit.
  const [narrow] = useState(isNarrowScreen);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const [printImage, setPrintImage] = useState<string | undefined>();

  useEffect(() => {
    if (!token) {
      setError('This invoice link is incomplete.');
      setLoading(false);
      return;
    }
    Promise.all([
      getOrderByInvoiceToken(token),
      getPaymentMethods().catch(() => [] as PaymentMethodOption[]),
      getPublicSettings().catch(() => ({} as Record<string, string>)),
    ])
      .then(([o, methods, settings]) => {
        setOrder(o);
        setPaymentMethods(methods);
        setBusiness(settings ?? {});
      })
      .catch(e =>
        setError(
          e?.response?.status === 404
            ? "We couldn't find that invoice. The link may have expired or been mistyped."
            : describeApiError(e, 'Could not load this invoice')
        )
      )
      .finally(() => setLoading(false));
  }, [token]);

  // Drawn ahead of time so Print can stay a plain window.print() inside the
  // tap. Until it's ready, printing falls back to the page itself.
  useEffect(() => {
    if (!narrow || !order) return;
    let url: string | undefined;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (!imageRef.current) return;
      renderInvoiceImage(imageRef.current)
        .then(blob => {
          if (cancelled) return;
          url = URL.createObjectURL(blob);
          setPrintImage(url);
        })
        .catch(e => console.error('Print image failed', e));
    }, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (url) URL.revokeObjectURL(url);
    };
  }, [narrow, order, paymentMethods, business]);

  if (loading) return <div className="max-w-xl mx-auto px-4 py-16 text-text-light">Loading…</div>;

  if (error || !order) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl mb-2">Invoice unavailable</h1>
        <p className="text-text-light">{error}</p>
      </div>
    );
  }

  const awaitingPayment = order.status === 'invoice_sent';
  const isPaid = Boolean(order.paid_at);
  const cardOffered =
    (order.accepted_payment_methods ?? []).includes(STRIPE_METHOD_KEY) && order.payment_link_url;

  return (
    // Print margins live here rather than in @page, which is set to zero so
    // browsers leave off their own header and footer (date, URL, page count).
    // Slimmer for the phone's picture, since iOS adds margins of its own.
    <div
      className={`bg-background py-8 px-4 print:bg-white ${
        printImage ? 'print:p-[6mm]' : 'print:p-[12mm]'
      }`}
    >
      <div className="max-w-[850px] mx-auto mb-4 flex items-center justify-between gap-3 print-hide">
        <div className="text-sm">
          {isPaid ? (
            <span className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-900 rounded px-3 py-1.5">
              Paid — thank you!
            </span>
          ) : (
            <span className="text-text-light">
              Amount due <strong className="text-text">{formatCents(order.total_cents)}</strong>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {awaitingPayment && cardOffered && (
            <a
              href={order.payment_link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-white rounded px-4 py-2 text-sm font-medium hover:bg-primary-dark"
            >
              <CreditCard size={15} /> Pay by card
            </a>
          )}
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-background-dark bg-white rounded px-4 py-2 text-sm hover:border-primary"
          >
            <Printer size={15} /> Print / save PDF
          </button>
        </div>
      </div>

      <div className={printImage ? 'print:hidden' : undefined}>
        <InvoiceDocument
          order={order}
          paymentMethods={paymentMethods}
          business={invoiceBusiness(business)}
        />
      </div>

      {printImage && (
        <img
          src={printImage}
          alt={`Invoice #${String(order.invoice_number ?? 0).padStart(3, '0')}`}
          className="invoice-print-image hidden print:block"
        />
      )}

      {/* Source for the print image: the full-width layout, drawn off the
          screen (a display:none element has nothing to draw). */}
      {narrow && (
        <div
          aria-hidden="true"
          className="invoice-wide print-hide fixed top-0 -left-[10000px] w-[850px] pointer-events-none"
        >
          <div ref={imageRef}>
            <InvoiceDocument
              order={order}
              paymentMethods={paymentMethods}
              business={invoiceBusiness(business)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;
